/* global bootstrap */
import { watch, computed } from 'vue'
import BpmOsgpoModule from '@/helpers/Modules/Product/BpmOsgpoModule'
import permissions from '@/helpers/permissions'

export function useBpmPolicyWatchers ({
  route,
  store,
  policy,
  duration,
  notify,
  nextTick,
  saveDraftTimeout,
  clientModalState,
  sharedApi,
  treatyMinDate,
  treatyStartDate,
}) {
  const currentUser = computed(() => sharedApi.user?.value ?? sharedApi.user)

  // Product mode
  watch(() => route.query.type, () => {
    sharedApi.syncLayoutPageTitle()
    sharedApi.applyModeTreatySettings()
  })

  // Draft autosave
  watch(
    policy,
    () => {
      if (policy.insurer) {
        if (saveDraftTimeout.value) {
          clearTimeout(saveDraftTimeout.value)
        }
        saveDraftTimeout.value = setTimeout(() => {
          sharedApi.saveDraft()
        }, 1000)
      }
    },
    { deep: true },
  )

  // Insurer as driver toggle
  watch(
    () => policy.isDrive,
    (newValue) => {
      if (newValue) {
        if (policy.insurer) {
          sharedApi.enrichInsurerBeforeIsDrive()
          if (!policy.insurer.licence) {
            if (
              policy.business_process.some(process =>
                process.type === 2
                && BpmOsgpoModule.BusinessProcess.isStarted(process)
                && process?.init_data?.iin === policy.insurer?.client?.iin,
              )
            ) {
              notify.error({ message: 'Страхователь уже добавлен в качестве водителя.' })
              nextTick(() => {
                policy.isDrive = false
              })
              return
            }
            notify.error({ message: 'Водительские права не заполнены. Пожалуйста, внесите номер ВУ' })
            nextTick(() => {
              policy.isDrive = false
              clientModalState.clientType = 2
              clientModalState.data = {
                phone: policy.insurer?.client?.phone ?? '',
                iin: policy.insurer?.client?.iin ?? '',
                nomer_vu: policy.insurer?.licence?.number ?? '',
                isPdl: false,
                isSocial: false,
              }
              clientModalState.key = null
              clientModalState.step = 0
              new bootstrap.Modal('#customerBpmModal', {}).show()
            })
            return
          }
          const idx = policy.insureds.findIndex(insured => sharedApi.isSameClient(insured, policy.insurer))
          if (idx === -1) {
            policy.insureds.unshift(policy.insurer)
          } else if (policy.insureds[idx] !== policy.insurer) {
            policy.insureds.splice(idx, 1, policy.insurer)
          }
        }
      } else {
        if (policy.insurer) {
          if (!policy.insurer.licence) {
            if (
              policy.business_process.some(process =>
                process.type === 2
                && BpmOsgpoModule.BusinessProcess.isStarted(process)
                && process?.init_data?.iin === policy.insurer?.client?.iin,
              )
            ) {
              nextTick(() => {
                policy.isDrive = false
              })
              return
            }
          }
          const insurerIndex = policy.insureds.findIndex(insured => sharedApi.isSameClient(insured, policy.insurer))
          if (insurerIndex !== -1) {
            policy.insureds.splice(insurerIndex, 1)
          }
        }
      }
      sharedApi.removeUnfinishedInsuredBusinessProcessByInsurer()
    },
    { immediate: true },
  )

  // Premium recalculation triggers
  watch(
    () => policy.business_process,
    (newValue, oldValue) => {
      sharedApi.syncAddedLists()
      const toSignature = (arr) => {
        const critical = (arr || []).filter(p => p.type !== 5).map(p => ({ id: p.id, type: p.type }))
        return JSON.stringify(critical.sort((a, b) => String(a.id).localeCompare(String(b.id))))
      }
      if (toSignature(oldValue) !== toSignature(newValue)) {
        sharedApi.handlePolicyCriticalDataChanges(newValue)
      }
    },
    { deep: true },
  )

  watch(
    () => policy.dgpo,
    (newValue) => {
      sharedApi.syncAddedLists()
      sharedApi.handlePolicyCriticalDataChanges(newValue)
    },
    { deep: true },
  )

  watch(
    () => policy.insureds,
    (newValue) => {
      sharedApi.syncAddedLists()
      sharedApi.handlePolicyCriticalDataChanges(newValue)
    },
    { deep: true },
  )

  watch(
    () => policy.insurer,
    (newValue) => {
      sharedApi.syncAddedLists()
      sharedApi.handlePolicyCriticalDataChanges(newValue)
      if (newValue && policy.isDrive) {
        const idx = policy.insureds.findIndex(insured => sharedApi.isSameClient(insured, newValue))
        if (idx === -1) {
          policy.insureds.unshift(newValue)
        } else if (policy.insureds[idx] !== newValue) {
          policy.insureds.splice(idx, 1, newValue)
        }
      }
      if (!newValue && policy.isDrive) {
        policy.isDrive = false
      }
    },
    { deep: true },
  )

  watch(
    () => policy.vehicle,
    (newValue) => {
      sharedApi.syncAddedLists()
      sharedApi.handlePolicyCriticalDataChanges(newValue)
    },
    { deep: true },
  )

  // Order dictionaries
  watch(
    () => policy.manager,
    (val) => {
      sharedApi.loadAgentsByManager(val)
    },
    { immediate: true },
  )

  // Treaty dates & duration
  watch(
    () => policy.treaty,
    (newValue) => {
      sharedApi.handlePolicyCriticalDataChanges(newValue)
    },
    { immediate: true, deep: true },
  )

  watch(
    () => policy.treaty?.term,
    (newValue) => {
      if (!newValue) {
        duration.value = null
        return
      }
      if (!BpmOsgpoModule.Treaty.isEndDate(newValue)) {
        return
      }
      if (sharedApi.clampTreatyEndDate()) {
        return
      }
      if (
        duration.value &&
        BpmOsgpoModule.Treaty.calculateEndDate(policy.treaty.date, duration.value) !== newValue
      ) {
        duration.value = null
      }
    },
    { immediate: true },
  )

  watch(
    () => policy.treaty?.date,
    (newValue) => {
      if (!newValue) {
        duration.value = null
        return
      }
      if (newValue < treatyMinDate.value) {
        policy.treaty.date = treatyMinDate.value
        return
      }
      sharedApi.syncTreatyTerm()
      sharedApi.clampTreatyEndDate()
    },
    { immediate: true },
  )

  watch(
    duration,
    () => {
      sharedApi.syncTreatyTerm()
    },
    { immediate: true },
  )

  // Смена дня на открытой странице: дата начала не может остаться меньше минимальной
  watch(
    treatyMinDate,
    (minDate) => {
      if (minDate && policy.treaty?.date && policy.treaty.date < minDate) {
        policy.treaty.date = minDate
      }
    },
  )

  // Route query: reset policy when no draft/rewrite, reload when id changes
  watch(
    () => route.query.draft,
    (newValue, oldValue) => {
      if (newValue && newValue !== oldValue && oldValue !== undefined) {
        sharedApi.loadDraft()
      } else if (!newValue && !route.query.ulid) {
        store.dispatch('bpmResetWizard', { treatyDate: treatyStartDate.value })
        sharedApi.applyModeTreatySettings()
      }
      sharedApi.setPolicyApc()
    },
    { immediate: true },
  )

  watch(
    () => route.query.ulid,
    (newValue, oldValue) => {
      if (newValue && newValue !== oldValue && oldValue !== undefined) {
        store.dispatch('bpmResetWizard', { treatyDate: treatyStartDate.value })
        sharedApi.loadRewritePolicy(newValue)
      } else if (!newValue && !route.query.draft) {
        store.dispatch('bpmResetWizard', { treatyDate: treatyStartDate.value })
        sharedApi.applyModeTreatySettings()
      }
      sharedApi.setPolicyApc()
    },
    { immediate: true },
  )

  // Terminal availability alert
  watch(
    currentUser,
    (newValue) => {
      if (newValue && !newValue.hasTerminal && permissions.groupSale().includes(newValue.role) && newValue.role !== 0) {
        setTimeout(() => {
          new bootstrap.Modal('#alertModal', {}).show()
        }, 150)
      }
    },
    { immediate: true },
  )
}
